import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { motion } from "framer-motion";
import { Camera } from "lucide-react";
import { api } from "@/lib/api";
import { RevealLines, FadeUp } from "@/components/Reveal";

export default function Photojournalism() {
  const [stories, setStories] = useState([]);
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(-1);

  useEffect(() => {
    api.get("/photostories").then(({ data }) => setStories(data)).catch(() => {});
  }, []);

  // Flatten all images into a single lightbox set
  const allImages = stories.flatMap((s) =>
    (s.images || []).map((url) => ({ src: url, title: s.title, description: s.location }))
  );

  const openAt = (url) => {
    const i = allImages.findIndex((im) => im.src === url);
    setSlides(allImages);
    setIndex(i >= 0 ? i : 0);
  };

  return (
    <div data-testid="photojournalism-page" className="bg-ink text-white">
      <Seo title="Photojournalism" description="Visual records of the hours the economy forgets to count — full-bleed photojournalism from The Canary Report." />
      {/* Header */}
      <section className="relative grain overflow-hidden pt-[120px] pb-16 lg:pb-24">
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <p className="label text-canary mb-6">Photojournalism</p>
          <RevealLines
            as="h1"
            className="font-display font-black text-[13vw] lg:text-[8vw] leading-[0.85] tracking-tight"
            lines={["Seen, not", "spoken for."]}
          />
          <FadeUp delay={0.6}>
            <p className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed">
              Visual records of the hours the economy forgets to count. Click any frame to enter
              the full-screen gallery.
            </p>
            <Link to="/photo-of-the-month" data-testid="photojournalism-potm-link"
              className="inline-flex items-center gap-2 mt-7 label bg-canary text-ink px-7 py-3.5 rounded-full hover:bg-white transition-colors duration-300">
              <Camera size={15} /> Canary Photo of the Month
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* Immersive full-bleed grid */}
      <div className="space-y-24 lg:space-y-36 pb-28">
        {stories.map((story, si) => (
          <section key={story.id} data-testid={`photo-story-${story.id}`}>
            <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
              <FadeUp className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 border-b border-white/15 pb-6">
                <div>
                  <p className="label text-white/40 mb-3">{story.location}</p>
                  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                    {story.title}
                  </h2>
                </div>
                <p className="max-w-md text-white/60 leading-relaxed">{story.description}</p>
              </FadeUp>
            </div>

            {/* asymmetric mosaic */}
            {(story.images || []).length === 0 ? (
              <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
                <div className="border border-white/15 bg-white/5 aspect-[16/6] grid place-items-center">
                  <p className="label text-white/40">Gallery coming soon</p>
                </div>
              </div>
            ) : (
            <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-2 lg:grid-cols-12 gap-3 lg:gap-4">
              {(story.images || []).map((url, i) => {
                const spans = [
                  "col-span-2 lg:col-span-7 aspect-[16/10]",
                  "col-span-1 lg:col-span-5 aspect-[4/5]",
                  "col-span-2 lg:col-span-12 aspect-[16/7]",
                ];
                const cls = spans[i % spans.length];
                return (
                  <motion.button
                    key={i}
                    onClick={() => openAt(url)}
                    data-testid={`photo-thumb-${story.id}-${i}`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.08 }}
                    className={`hover-img group relative bg-white/5 ${cls}`}
                  >
                    <img src={url} alt={`${story.title} ${i + 1}`} className="h-full w-full object-cover" />
                    <span className="absolute top-3 left-3 label text-white/0 group-hover:text-white transition-colors bg-ink/60 px-2 py-1">
                      {String(si + 1).padStart(2, "0")}·{String(i + 1).padStart(2, "0")}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            )}
          </section>
        ))}
      </div>

      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={index}
        slides={slides}
        styles={{ container: { backgroundColor: "rgba(17,17,17,0.97)" } }}
      />
    </div>
  );
}
